import React, { useEffect, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";
import axios from "../../API/axios";
import MoreHorizSharpIcon from '@mui/icons-material/MoreHorizSharp';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import PushPinSharpIcon from '@mui/icons-material/PushPinSharp';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import DeleteOutlineSharpIcon from '@mui/icons-material/DeleteOutlineSharp';
import { createSvgIcon } from '@mui/material/utils';
import { useTranslation } from "react-i18next";
import { invalidateMessageList } from "../../Services/apiService";
import MessageConfirmModal from "./MessageConfirmModal";

const PinOffOutlinedIcon = createSvgIcon(
  <path d="M8 6.2V4H7V2h10v2h-1v8l2 2v2h-.2L14 12.2V4h-4v4.2L8 6.2zm12 14.5-1.3 1.3-5.9-5.9V22h-1.6v-6H6v-2l2-2v-.7L2 5.3 3.3 4 20 20.7zM8.8 14h1.8l-.9-.9-.9.9z" />,
  'PinOffOutlined'
);

const HeaderMenuItem = ({ label, icon, onClick }) => (
  <Dropdown.Item as="button" className="dropdownList">
    <Link
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      <div className="d-flex align-items-center justify-content-between w-100 chat-header-menu-item">
        <span className="dropdownTxt f-14">{label}</span>
        {icon}
      </div>
    </Link>
  </Dropdown.Item>
);

const MessageInteraction = ({
  isRead,
  chatId,
  isPined,
  getChatList,
  archiveMessage,
  msgId,
  usermsgId,
  getArchivedList,
  getMessageList,
  UserId,
  toUserUId,
  setChatList,
  variant = "list",
}) => {
  const sessionId = localStorage.getItem("sessionId");
  const toUserUID = localStorage.getItem("toUserUid");
  const dropdownRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const { t } = useTranslation();
  const [showDelete, setShowDelete] = useState(false);
  const [showClear, setShowClear] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const language = localStorage.getItem("Lang");

  const buildFullUserChatPayload = (extra = {}) => ({
    deviceType: "Web",
    sessionId: sessionId,
    msgID: usermsgId || msgId,
    toUserUid: toUserUId || UserId || toUserUID,
    langKey: language === "en" ? "" : language,
    ...extra,
  });

  const deleteFullUserChat = (url, payload) =>
    axios({
      method: "delete",
      url,
      data: payload,
      headers: {
        "Content-Type": "application/json",
      },
    });

  const showSuccessToast = (message) => {
    toast.success(message || t("modals.Success"));
  };

  const showWarningToast = (message) => {
    toast(message || t("modals.Warning"), { icon: "⚠️" });
  };

  const showErrorToast = (message) => {
    toast.error(message || t("modals.Error"));
  };

  const closeDropdown = () => setShowDropdown(false);

  const toggleDropdown = () => {
    setShowDropdown((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  useEffect(() => {
    if (showDelete || showClear) {
      setShowDropdown(false);
    }
  }, [showDelete, showClear]);

  const ArchiveChat = async () => {
    const responseData = {
      deviceType: "Web",
      sessionId: sessionId,
      msgID: usermsgId,
      langKey: language === "en" ? "" : language,
    };

    try {
      const res = await axios.post(`Message/ArchiveChat`, responseData);
      showSuccessToast(res.data.message);
      closeDropdown();
      getArchivedList();
      getMessageList({ forceRefresh: true, silent: true });
    } catch (e) {
      if (e.response && e.response.status === 404) {
        showWarningToast(e.response.data.message);
      } else {
        showErrorToast(e.response?.data?.message);
      }
    }
  };

  const UnArchiveChat = async () => {
    const responseData = {
      deviceType: "Web",
      sessionId: sessionId,
      msgID: msgId,
      langKey: language === "en" ? "" : language,
    };

    try {
      const res = await axios.post(`Message/UnArchiveChat`, responseData);
      showSuccessToast(res.data.message);
      closeDropdown();
      getArchivedList();
      getMessageList({ forceRefresh: true, silent: true });
    } catch (e) {
      showErrorToast(e.response?.data?.message);
    }
  };

  const PinChat = async () => {
    const responseData = {
      deviceType: "Web",
      sessionId: sessionId,
      msgID: usermsgId,
      langKey: language === "en" ? "" : language,
    };

    try {
      const res = await axios.post(`Message/PinMessage`, responseData);
      showSuccessToast(res.data.message);
      closeDropdown();
      invalidateMessageList();
      getArchivedList();
      getMessageList({ forceRefresh: true, silent: true });
    } catch (e) {
      showErrorToast(e.response?.data?.message);
    }
  };

  const UnPinChat = async () => {
    const responseData = {
      deviceType: "Web",
      sessionId: sessionId,
      msgID: usermsgId,
      langKey: language === "en" ? "" : language,
    };

    try {
      const res = await axios.post(`Message/UnPinMessage`, responseData);
      showSuccessToast(res.data.message);
      closeDropdown();
      invalidateMessageList();
      getArchivedList();
      getMessageList({ forceRefresh: true, silent: true });
    } catch (e) {
      showErrorToast(e.response?.data?.message);
    }
  };

  const DeleteChat = async () => {
    const responseData = buildFullUserChatPayload({ forEveryOne: false });

    setIsSubmitting(true);
    try {
      const res = await deleteFullUserChat(`Message/DeleteFullUserChat`, responseData);
      setShowDelete(false);
      showSuccessToast(res.data.message);
      getArchivedList();
      getMessageList({ forceRefresh: true, silent: true });
      if (typeof setChatList === "function") {
        setChatList([]);
      }
    } catch (e) {
      if (e.response && e.response.status === 404) {
        showWarningToast(e.response.data.message);
      } else {
        showErrorToast(e.response?.data?.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const ClearChat = async () => {
    const responseData = buildFullUserChatPayload();

    setIsSubmitting(true);
    try {
      const res = await deleteFullUserChat(`Message/ClearFullUserChat`, responseData);
      setShowClear(false);
      showSuccessToast(res.data.message);
      getArchivedList();
      if (typeof setChatList === "function") {
        setChatList([]);
      }
      getMessageList({ forceRefresh: true, silent: true });
    } catch (e) {
      if (e.response && e.response.status === 404) {
        showWarningToast(e.response.data.message);
      } else {
        showErrorToast(e.response?.data?.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isHeaderVariant = variant === "header";

  const renderHeaderMenu = () => (
    <>
      {archiveMessage === false ? (
        <HeaderMenuItem
          label={t("Notifications.Archive Chat")}
          icon={<ArchiveOutlinedIcon className="material-symbols-sharp" />}
          onClick={() => {
            ArchiveChat();
          }}
        />
      ) : (
        <HeaderMenuItem
          label={t("Notifications.UnArchive Chat")}
          icon={<UnarchiveOutlinedIcon className="material-symbols-sharp" />}
          onClick={() => {
            UnArchiveChat();
          }}
        />
      )}

      {archiveMessage === false && isPined === false ? (
        <HeaderMenuItem
          label={t("Notifications.Pin Chat")}
          icon={<PushPinSharpIcon className="material-symbols-sharp" />}
          onClick={() => {
            PinChat();
          }}
        />
      ) : isPined === true ? (
        <HeaderMenuItem
          label={t("Notifications.UnPin Chat")}
          icon={<PinOffOutlinedIcon className="material-symbols-sharp" />}
          onClick={() => {
            UnPinChat();
          }}
        />
      ) : null}

      <HeaderMenuItem
        label={t("Notifications.Delete Message")}
        icon={<DeleteOutlineSharpIcon className="material-symbols-sharp" />}
        onClick={() => {
          setShowDelete(true);
          closeDropdown();
        }}
      />
    </>
  );

  return (
    <>
      <div
        className={`postMenu pointer${showDropdown ? " menu-open" : ""}${isHeaderVariant ? " chat-header-menu-trigger" : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          toggleDropdown();
        }}
      >
        <MoreHorizSharpIcon className="material-symbols-sharp" />
        {showDropdown && !showDelete && !showClear && (
          <Dropdown.Menu
            show={showDropdown}
            ref={dropdownRef}
            className={`InteractionModal${isHeaderVariant ? " chat-header-menu" : " message-list-menu"}`}
            popperConfig={
              isHeaderVariant
                ? undefined
                : {
                    strategy: "fixed",
                    modifiers: [
                      {
                        name: "preventOverflow",
                        options: { padding: 8, boundary: "viewport" },
                      },
                      {
                        name: "flip",
                        options: { fallbackPlacements: ["top", "bottom"] },
                      },
                    ],
                  }
            }
          >
            {isHeaderVariant ? (
              renderHeaderMenu()
            ) : (
              <>
                {archiveMessage === false ? (
                  <Dropdown.Item as="button" className="dropdownList">
                    <Link onClick={() => ArchiveChat()}>
                      <div className="d-flex align-items-center">
                        <ArchiveOutlinedIcon className="material-symbols-sharp me-2" />
                        <span className="dropdownTxt f-14">{t("Notifications.Archive Chat")}</span>
                      </div>
                    </Link>
                  </Dropdown.Item>
                ) : (
                  <Dropdown.Item as="button" className="dropdownList">
                    <Link onClick={() => UnArchiveChat()}>
                      <div className="d-flex align-items-center">
                        <UnarchiveOutlinedIcon className="material-symbols-sharp me-2" />
                        <span className="dropdownTxt f-14">{t("Notifications.UnArchive Chat")}</span>
                      </div>
                    </Link>
                  </Dropdown.Item>
                )}

                {archiveMessage === false && isPined === false ? (
                  <Dropdown.Item as="button" className="dropdownList">
                    <Link onClick={() => PinChat()}>
                      <div className="d-flex align-items-center">
                        <PushPinSharpIcon className="material-symbols-sharp me-2" />
                        <span className="dropdownTxt f-14">{t("Notifications.Pin Chat")}</span>
                      </div>
                    </Link>
                  </Dropdown.Item>
                ) : isPined === true ? (
                  <Dropdown.Item as="button" className="dropdownList">
                    <Link onClick={() => UnPinChat()}>
                      <div className="d-flex align-items-center">
                        <PinOffOutlinedIcon className="material-symbols-sharp me-2" />
                        <span className="dropdownTxt f-14">{t("Notifications.UnPin Chat")}</span>
                      </div>
                    </Link>
                  </Dropdown.Item>
                ) : null}

                <Dropdown.Item as="button" className="dropdownList">
                  <Link
                    onClick={() => {
                      setShowClear(true);
                      closeDropdown();
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <RemoveCircleOutlineOutlinedIcon className="material-symbols-sharp me-2" />
                      <span className="dropdownTxt f-14 redText">{t("Notifications.Clear Message")}</span>
                    </div>
                  </Link>
                </Dropdown.Item>

                <Dropdown.Item as="button" className="dropdownList">
                  <Link
                    onClick={() => {
                      setShowDelete(true);
                      closeDropdown();
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <DeleteOutlineSharpIcon className="material-symbols-sharp me-2" />
                      <span className="dropdownTxt f-14 redText">{t("Notifications.Delete Message")}</span>
                    </div>
                  </Link>
                </Dropdown.Item>
              </>
            )}
          </Dropdown.Menu>
        )}
      </div>

      <MessageConfirmModal
        show={showDelete}
        title={t("User Profile.Are You Sure You want to Delete?")}
        confirmLabel={t("Drafts.Delete")}
        cancelLabel={t("Connections.Cancel")}
        variant="danger"
        isLoading={isSubmitting}
        onClose={() => !isSubmitting && setShowDelete(false)}
        onConfirm={DeleteChat}
      />

      <MessageConfirmModal
        show={showClear}
        title={t("modals.Are you sure you want to Clear Chat?")}
        confirmLabel={t("modals.Yes")}
        cancelLabel={t("Connections.Cancel")}
        variant="primary"
        isLoading={isSubmitting}
        onClose={() => !isSubmitting && setShowClear(false)}
        onConfirm={ClearChat}
      />
    </>
  );
};

export default MessageInteraction;
